import { CumulativeProjectDailyStats } from '../../../../../pages/projects/preview/models/CumulativeProjectDailyStats';
import { ProjectDailyStatsResponse } from '../../../../models/projects/stats/ProjectDailyStatsResponse';
import { client } from '../../../../services/apiClient';
import { ChatType } from '../../../../types/monitor/ChatType';
import { getDefaultRange } from '../../../../utils/utils';
import { format } from 'date-fns';
import {
  AllBrandsStatsResult,
  ProjectSovPoint,
  ProjectVisibilityBrandPoint,
  ProjectVisibilityPerMonitorPoint,
  ProjectVisibilityPoint,
} from '../../../../models/projects/stats/AllBrandsStatsResponse';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';

const PROJECT_DAILY_STATS_ENDPOINT = '/projects/daily-stats';

export type ExportProjectBrandScoreRequest = {
  projectId: number;
  period: MonitorPeriod;
  chatType: ChatType;
  startDate: string;
  endDate: string;
};

export const getProjectCumulativeDailyStats = async (
  cumulativeProjectDailyStats: CumulativeProjectDailyStats | null
): Promise<ProjectDailyStatsResponse> => {
  if (!cumulativeProjectDailyStats) {
    return {} as ProjectDailyStatsResponse;
  }

  const { data } = await client(true, true).post<ProjectDailyStatsResponse>(
    PROJECT_DAILY_STATS_ENDPOINT,
    cumulativeProjectDailyStats
  );
  return data;
};

export const getProjectAllBrandsStats = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[],
  sovPerPrompt?: boolean,
  allBrands?: boolean
): Promise<AllBrandsStatsResult> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam = filterChatTypes && filterChatTypes.length > 0
    ? `?models=${filterChatTypes.join(',')}`
    : '';

  const promptIdsParam = monitorPromptIds && monitorPromptIds.length > 0
    ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
    : '';

  // V2-only: switch SoV to the 0-filled per-prompt average (see backend ?sovMode=perPrompt).
  const sovModeParam = sovPerPrompt
    ? `${modelsParam || promptIdsParam ? '&' : '?'}sovMode=perPrompt`
    : '';

  // Prompt view V2 only: skip the project-level picked-brands narrowing (backend ?allBrands=true).
  const allBrandsParam = allBrands
    ? `${modelsParam || promptIdsParam || sovModeParam ? '&' : '?'}allBrands=true`
    : '';

  const { data } = await client(true, true).get<AllBrandsStatsResult>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/all-brands-stats/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}${sovModeParam}${allBrandsParam}`
  );
  return data;
};

export const getProjectVisibilityChart = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectVisibilityPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectVisibilityPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/visibility-chart/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export const getProjectVisibilityChartPerBrand = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectVisibilityBrandPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectVisibilityBrandPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/visibility-chart-per-brand/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export const getProjectVisibilityPerMonitor = async (
  id: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectVisibilityPerMonitorPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectVisibilityPerMonitorPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/visibility-per-monitor/${id}/${chatType}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export interface ProjectSovPerModelPoint {
  chatType: string;
  keyword: string;
  shareOfVoice: number;
  // Brand's average position in this model. The question stats page derives SoV from this position
  // (SCORE_WEIGHT, then normalised within the model); other pages ignore it.
  position?: number;
}

// Per-(model, brand) SoV feeding the "Share of Voice by AI Chatbots" bar chart, in the flavour that
// matches the calling page's own pie: per-prompt averaging (V2) or `pooled` SUM/SUM (V1). Same
// query-string shape as the other per-monitor/per-model endpoints.
export const getProjectSovPerModel = async (
  id: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[],
  sovMode?: 'pooled'
): Promise<ProjectSovPerModelPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const sovModeParam = sovMode
    ? `${modelsParam || promptIdsParam ? '&' : '?'}sovMode=${sovMode}`
    : '';

  const { data } = await client(true, true).get<ProjectSovPerModelPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/sov-per-model/${id}/${chatType}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}${sovModeParam}`
  );
  return data;
};

export const getProjectSovTimeSeries = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectSovPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectSovPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/sov-time-series/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export interface ProjectModelDataDateRange {
  model: string;
  firstDate: string;
  lastDate: string;
}

// First/last date each model produced data for the project, within the given range. Used to show the
// real data span of an archival model in the prompts table tooltip.
export const getProjectModelDataDateRanges = async (
  id: number,
  from?: Date | null,
  to?: Date | null
): Promise<ProjectModelDataDateRange[]> => {
  const defaultRange = getDefaultRange();
  const fromDate = from ? new Date(from) : defaultRange[0];
  const toDate = to ? new Date(to) : defaultRange[1];
  const formattedFrom = format(fromDate, 'yyyy-MM-dd');
  const formattedTo = format(toDate, 'yyyy-MM-dd');

  const { data } = await client(true, true).get<ProjectModelDataDateRange[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/model-data-date-ranges/${id}/${formattedFrom}/${formattedTo}`
  );
  return data;
};

// Tag-resolved keys of the project's "live" brands (have data under the account's current models),
// independent of the project_options.keywords selection. Used to hide orphan brands from the
// brand-selection option lists.
export const getProjectLiveBrandKeys = async (
  id: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<string[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';
  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<{ liveKeys: string[] }>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/live-brand-keys/${id}/${chatType}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data.liveKeys ?? [];
};

export const exportProjectStats = async (
  projectId: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  return client(true, true)
    .get<Blob>(
      `${PROJECT_DAILY_STATS_ENDPOINT}/export-project-stats/${projectId}/${formattedFrom}/${formattedTo}/${chatType}`,
      {
        responseType: 'blob',
      }
    )
    .then((response) => response.data)
    .catch((error) => error);
};

export const exportProjectBrandScore = async (
  exportProjectBrandScoreRequest: ExportProjectBrandScoreRequest | null
): Promise<Blob> => {
  if (!exportProjectBrandScoreRequest) {
    throw Error('Invalid data');
  }

  const { projectId, period, chatType, startDate, endDate } = exportProjectBrandScoreRequest;
  const { data } = await client(true, true).get<Blob>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/export-brand-score/${projectId}/${startDate}/${endDate}/${chatType}/${period}`,
    {
      responseType: 'blob',
    }
  );

  return data;
};

export type ProjectListKpi = {
  brandScore: number;
  medianPosition: number;
  averagePosition: number;
  visibilityPercent: number;
  /** Percentage points vs the preceding window of the same length; null without data (2026-10-22). */
  visibilityPercentTrend: number | null;
  brandScoreTrend: number | string;
  medianPositionTrend: number | string;
  averagePositionTrend: number | string;
};

export const getProjectsListKpis = async (
  ids: number[],
  from: Date,
  to: Date,
  withVisibilityTrend = false
): Promise<Record<number, ProjectListKpi>> => {
  if (ids.length === 0) {
    return {};
  }

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const { data } = await client(true, true).get<Record<number, ProjectListKpi>>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/list-kpis?ids=${ids.join(',')}&from=${formattedFrom}&to=${formattedTo}${
      withVisibilityTrend ? '&withVisibilityTrend=true' : ''
    }`
  );
  return data;
};
